/**
 * Civil date + time in an IANA zone → UTC instant (TMMAPP-0014).
 * Built with `Intl.DateTimeFormat#formatToParts` iteration — never `new Date('YYYY-MM-DD…')`.
 */

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

interface CivilParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function partsOf(ms: number, zone: string): CivilParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((p) => p.type === type)?.value;
    return value === undefined ? Number.NaN : Number(value);
  };
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function asUtcGuess(p: CivilParts): number {
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** The zone's offset from UTC at `ms` (whole seconds), in milliseconds. */
function offsetAt(ms: number, zone: string): number {
  const second = Math.floor(ms / 1000) * 1000;
  return asUtcGuess(partsOf(second, zone)) - second;
}

/**
 * UTC ISO instant for a civil `YYYY-MM-DD` + `HH:MM` (optional seconds) in `zone`.
 * A spring-forward gap snaps to the first valid instant after the gap; a fall-back overlap
 * resolves to the earlier offset (the first time that wall clock occurs).
 */
export function zonedCivilToUtc(date: string, time: string, zone: string): string {
  const dm = DATE.exec(date);
  const tm = TIME.exec(time);
  if (dm === null || tm === null) throw new Error(`Invalid civil date/time: ${date} ${time}`);
  const want: CivilParts = {
    year: Number(dm[1]),
    month: Number(dm[2]),
    day: Number(dm[3]),
    hour: Number(tm[1]),
    minute: Number(tm[2]),
    second: Number(tm[3] ?? '0'),
  };
  const wall = asUtcGuess(want);
  // The zone's offsets a day either side: one transition at most falls between them.
  const before = offsetAt(wall - DAY_MS, zone);
  const after = offsetAt(wall + DAY_MS, zone);
  // Each offset that reads back as the wanted wall clock is a valid instant; an overlap has two,
  // and the earlier instant (the first time that wall clock occurs) wins.
  const valid = [...new Set([before, after])]
    .map((offset) => wall - offset)
    .filter((utc) => asUtcGuess(partsOf(utc, zone)) === wall)
    .sort((a, b) => a - b);
  if (valid[0] !== undefined) return new Date(valid[0]).toISOString();
  // A gap (TMMAPP-0162 item 4): the first valid instant after it is the transition itself, the
  // first minute on the later offset, found between the wall clock read on either offset.
  let lo = Math.floor((wall - after) / MINUTE_MS);
  let hi = Math.ceil((wall - before) / MINUTE_MS);
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (offsetAt(mid * MINUTE_MS, zone) === after) hi = mid;
    else lo = mid;
  }
  return new Date(hi * MINUTE_MS).toISOString();
}

/** Civil `YYYY-MM-DD` and `HH:MM` of a UTC instant in `zone`. */
export function utcToZonedCivil(iso: string, zone: string): { date: string; time: string } {
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) throw new Error(`Not an instant: ${iso}`);
  const p = partsOf(ms, zone);
  return {
    date: `${String(p.year).padStart(4, '0')}-${pad2(p.month)}-${pad2(p.day)}`,
    time: `${pad2(p.hour)}:${pad2(p.minute)}`,
  };
}

/** The civil day after `date` (calendar arithmetic on UTC fields, no zone involved). */
function nextCivilDay(date: string): string {
  const dm = DATE.exec(date);
  if (dm === null) throw new Error(`Invalid civil date: ${date}`);
  const next = new Date(Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]) + 1));
  return `${String(next.getUTCFullYear()).padStart(4, '0')}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`;
}

/**
 * The instant `date` starts in `zone`. Where a DST jump skips midnight (America/Santiago in
 * September), the day starts at the jump (`zonedCivilToUtc` settles a gap there); a landing on the
 * evening before is moved forward to it.
 */
function civilDayStartUtc(date: string, zone: string): string {
  const iso = zonedCivilToUtc(date, '00:00', zone);
  const local = utcToZonedCivil(iso, zone);
  if (local.date >= date) return iso;
  const [hour = 0, minute = 0] = local.time.split(':').map(Number);
  return new Date(Date.parse(iso) + (24 * 60 - (hour * 60 + minute)) * 60_000).toISOString();
}

/**
 * A civil `from` / `to` filter → the date-time range the contract takes (TMMAPP-0161): `from` is the
 * instant the day starts in `zone`, `to` the instant the NEXT day starts there (the end of `to`).
 * Blank or absent bounds stay absent; a value that is not a civil date (already an instant) passes
 * through.
 */
export function civilRangeToInstants(
  range: { from?: string | undefined; to?: string | undefined },
  zone: string,
): { from: string | undefined; to: string | undefined } {
  const tz = zone.length > 0 ? zone : 'UTC';
  const convert = (value: string | undefined, next: boolean): string | undefined => {
    if (value === undefined || value.length === 0) return undefined;
    if (!DATE.test(value)) return value;
    return civilDayStartUtc(next ? nextCivilDay(value) : value, tz);
  };
  return { from: convert(range.from, false), to: convert(range.to, true) };
}

/** Today's civil date in `zone`. */
export function todayCivilInZone(zone: string): string {
  return utcToZonedCivil(new Date().toISOString(), zone).date;
}

/** True when two ISO instants name the same millisecond (`.000Z` vs `Z`). */
export function sameUtcInstant(left: string | null | undefined, right: string): boolean {
  if (left === undefined || left === null) return false;
  const a = Date.parse(left);
  const b = Date.parse(right);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
}
